import Head from 'next/head';
import { HeadingRegular, BodyR, Button, BodyM } from '@pensionbee/honeypot-ui';
import { ThemedImage } from '@/domains/theme/ThemedImage';
import router from 'next/router';
import Link from 'next/link';
import { useAuthContext } from '@/domains/auth/authContext';

type ErrorProps = {
  statusCode?: number;
};

function Error({ statusCode }: ErrorProps) {
  const {
    logout,
    isLoggedIn,
    authReady,
    auth: firebaseAuth,
  } = useAuthContext();

  const handleLogout = async () => {
    if (!authReady || !isLoggedIn || !firebaseAuth) return;

    try {
      const { err } = await logout();
      if (err) {
        console.warn('Log out failed', err);
        return;
      }

      void router.push('/hive/accounts/login');
    } catch (error) {
      console.warn('Log out failed', error);
    }
  };
  const errorMessage = statusCode
    ? `An error ${statusCode} occurred`
    : 'Something went wrong';

  const isLoggedOut = authReady && !isLoggedIn;

  return (
    <>
      <Head>
        <title>{errorMessage}</title>
      </Head>
      <div className="flex justify-center items-center mt-28">
        <ThemedImage name="errorIcon" alt="Error" width={120} height={120} />
      </div>
      <div className="pt-28">
        <HeadingRegular as="h1">{errorMessage}</HeadingRegular>
      </div>
      <div className="py-28">
        <BodyR>
          There seems to be a problem loading this page. If the problem
          persists,{' '}
          <Button
            variant="inline-link"
            as={Link}
            href="/hive/beehive/account/support"
          >
            please contact your BeeKeeper.
          </Button>
        </BodyR>
      </div>
      <div className="flex flex-col gap-20">
        <Button
          data-testid={
            isLoggedOut
              ? 'general-error-return-to-login'
              : 'general-error-return-to-summary'
          }
          variant="primary-yellow"
          className="w-full cursor-pointer"
          as={Link}
          href={isLoggedOut ? '/hive/accounts/login' : '/hive/beehive/summary'}
        >
          <BodyM>{isLoggedOut ? 'Return to login' : 'Return to Summary'}</BodyM>
        </Button>
        {authReady && isLoggedIn && firebaseAuth && (
          <Button
            data-testid="general-error-log-out"
            variant="secondary"
            className="w-full cursor-pointer"
            onClick={handleLogout}
          >
            <BodyM>Log out</BodyM>
          </Button>
        )}
      </div>
    </>
  );
}

export default Error;
